import React, { useState, useEffect } from 'react';
import { Button } from "@heroui/react";
import { toast } from 'react-toastify';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Plus, Edit, Trash } from 'assets/icons';
import ProductVariantTemplateModal from './components/productVariantTemplateModal';
import { CreateYallyProductModal } from './components/createYallyProductModal';
import { ArrowsClockwise } from '@phosphor-icons/react';

interface ProductVariantTemplate {
  id: number;
  name: string;
  description?: string;
  ledgerAccountCode: string;
  isActive: boolean;
  indexable: boolean;
  defaultPrice?: number;
  createdAt: string;
  updatedAt: string;
}

const ProductVariantTemplatesTab: React.FC = () => {
  const { session } = useSession();
  const [templates, setTemplates] = useState<ProductVariantTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProductVariantTemplate | null>(null);
  const [status, setStatus] = useState<any | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  // Creating a sjabloon and creating the products belong together — see the banner below.
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/companies/product-variant-templates`,
        { method: 'GET' },
        session
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
      // Fetch status mapping (templates x companies)
      try {
        const res = await fetchWithAuth(`${API_URL}/companies/product-variant-templates-status`, { method: 'GET' }, session);
        if (res.ok) {
          const st = await res.json();
          setStatus(st);
        }
      } catch (e) {
        console.warn('Kon status niet ophalen:', e);
      }
    } catch (error) {
      console.error('Error fetching product variant templates:', error);
      toast.error('Fout bij ophalen productsjablonen');
      setTemplates([]); // Ensure templates is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template?: ProductVariantTemplate) => {
    setEditingTemplate(template || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingTemplate(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Ben je zeker dat je deze productsjabloon wilt verwijderen?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/companies/product-variant-templates/${id}`,
        { method: 'DELETE' },
        session
      );

      if (response.ok) {
        toast.success('Productsjabloon verwijderd');
        fetchTemplates();
      } else {
        throw new Error('Fout bij verwijderen');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Fout bij verwijderen productsjabloon');
    } finally {
      setLoading(false);
    }
  };

  const syncTemplate = async (id: number) => {
    setSyncingId(id);
    try {
      const res = await fetchWithAuth(`${API_URL}/companies/product-variant-templates/${id}/sync`, { method: 'POST' }, session);
      if (res.ok) {
        const result = await res.json();
        toast.success(`Gesynchroniseerd: ${result.created} aangemaakt, ${result.skipped} overgeslagen`);
        await fetchTemplates();
      } else {
        toast.error('Fout bij synchroniseren');
      }
    } catch (e) {
      console.error(e);
      toast.error('Fout bij synchroniseren');
    } finally {
      setSyncingId(null);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-neutral-primary">Productsjablonen</h2>
        <Button
          color="primary"
          startContent={<Plus />}
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-moss-primary text-white"
        >
          Nieuw product
        </Button>
      </div>



      <div className="rounded-lg border border-moss-primary/20 bg-moss-primary/5 p-3 text-sm text-neutral-primary">
        Een <span className="font-medium">productsjabloon</span> is de definitie van een Yally
        product: naam + grootboekrekening. Per sjabloon bestaat er één{' '}
        <span className="font-medium">product per vennootschap</span> in Odoo — die zie je onder
        "Product varianten". Een nieuw product wordt hier meteen voor de gekozen vennootschappen
        aangemaakt, en elke vennootschap die later bijkomt krijgt alle sjablonen automatisch.
        Ontbreekt er toch één, dan vult "Synchroniseren" dat aan.
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-neutral-secondary-mid">Productsjablonen laden...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-brick-secondary-fine">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Naam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Grootboekrekening
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Indexeerbaar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Ontbrekende varianten
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-primary uppercase tracking-wider">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(templates) && templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-neutral-primary">
                          {template.name}
                        </div>
                        {template.description && (
                          <div className="text-sm text-neutral-secondary-mid">
                            {template.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-primary">
                      {template.ledgerAccountCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        template.indexable
                          ? 'bg-status-good text-white'
                          : 'bg-neutral-secondary-mid text-white'
                      }`}>
                        {template.indexable ? 'Ja' : 'Nee'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-primary">
                      {status ? (() => {
                        const companies = (status.companies || []) as Array<any>;
                        const missing = companies.filter((c: any) => !c.archived && !c.mergedWithCompanyId && !status.status?.[template.id]?.[c.id]?.exists);
                        return missing.length > 0 ? missing.map((c: any) => c.name).join(', ') : '—';
                      })() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Button size="sm" variant="flat" onPress={() => syncTemplate(template.id)} isDisabled={syncingId === template.id} startContent={<ArrowsClockwise />}>Synchroniseren</Button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        template.isActive
                          ? 'bg-status-good text-white'
                          : 'bg-neutral-secondary-mid text-white'
                      }`}>
                        {template.isActive ? 'Actief' : 'Inactief'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={() => handleOpenModal(template)}
                        className="text-neutral-primary hover:text-moss-primary"
                      >
                        <Edit />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={() => handleDelete(template.id)}
                        className="text-neutral-primary hover:text-red-600"
                      >
                        <Trash />
                      </Button>
                    </td>
                  </tr>
                ))}
                {(!Array.isArray(templates) || templates.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-neutral-secondary-mid">
                      Geen productsjablonen gevonden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateYallyProductModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        companies={(status?.companies || []) as any}
        onCreated={fetchTemplates}
      />

      <ProductVariantTemplateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onOpenChange={setIsModalOpen}
        refreshTemplates={fetchTemplates}
        editingTemplate={editingTemplate}
      />
    </div>
  );
};

export default ProductVariantTemplatesTab; 